import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { PLANS, getPlan } from '../lib/plans';

/**
 * useSubscription — hook that exposes the user's current plan, active
 * subscription, usage counters and helper guards for premium limits.
 *
 * Returns:
 *  - plan: PLAN object (from /lib/plans.js)
 *  - subscription: row from `subscriptions` (or null for free)
 *  - usage: { activeBusinesses, monthlySparks }
 *  - canCreateBusiness: bool
 *  - canCreateSpark: bool
 *  - upgradeReason: 'business_limit' | 'spark_limit' | null
 *  - refresh: () => void
 */
export const useSubscription = () => {
  const { user, profile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState({ activeBusinesses: 0, monthlySparks: 0 });
  const [loading, setLoading] = useState(true);

  const planId = profile?.plan_id || (
    profile?.role === 'entrepreneur' ? 'entrepreneur_free' : 'investor_free'
  );
  const plan = getPlan(planId);

  const fetchAll = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      // current subscription (most recent active/trialing)
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1);
      setSubscription((subs && subs[0]) || null);

      // usage counters via RPC (defined in supabase-schema-premium.sql)
      const [{ data: bizCount }, { data: sparkCount }] = await Promise.all([
        supabase.rpc('entrepreneur_active_business_count', { p_user_id: user.id }),
        supabase.rpc('investor_monthly_spark_count', { p_user_id: user.id }),
      ]);
      setUsage({
        activeBusinesses: Number(bizCount) || 0,
        monthlySparks: Number(sparkCount) || 0,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useSubscription] fetch error:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canCreateBusiness = (() => {
    const limit = plan.limits?.activeBusinesses ?? Infinity;
    return usage.activeBusinesses < limit;
  })();
  const canCreateSpark = (() => {
    const limit = plan.limits?.monthlySparks ?? Infinity;
    return usage.monthlySparks < limit;
  })();

  const upgradeReason =
    !canCreateBusiness ? 'business_limit' :
    !canCreateSpark ? 'spark_limit' :
    null;

  return {
    plan,
    subscription,
    usage,
    loading,
    canCreateBusiness,
    canCreateSpark,
    upgradeReason,
    isPro: !!profile?.is_pro,
    isElite: !!profile?.is_elite,
    refresh: fetchAll,
  };
};
